import ReactiveComponent, { watch } from '@utils/reactive-component';
import template, { items as selectOptions } from './templates';
import isFn from '@utils/is-function';

const CLASS_NAME_DISABLED = '-disabled';
const CLASS_NAME_INVALID = 'is-invalid';
const CLASS_NAME_HIGHLIGHT = '-highlight';
const CLASS_NAME_VALID = 'is-valid';

const EVENT_KEY = '_selectBox_';
const EVENT_CHANGE = `change.${EVENT_KEY}`;
const EVENT_VALIDATION = `${EVENT_CHANGE}_validate`;

const OPTION_DATA_SELECTOR = 'data-option';

/**
 * @typedef {{ text: string, value: string }} Option
 * 
 * @typedef {Object} DefaultOptions
 * @property {string?} ariaLabel
 * @property {string?} className
 * @property {id?} id
 * @property {string?} mods
 * @property {Array<Option>} options
 * @property {boolean} replaceOriginEl
 * @property {string?} unselectedText
 * @property {string} value
 */

/** @type {DefaultOptions} */
const DEFAULT_OPTIONS = {
    ariaLabel: null,
    className: null,
    id: null,
    mods: '',
    name: null,
    options: [],
    replaceOriginEl: true,
    unselectedText: null,
    value: ''
};

export default class SelectBox extends ReactiveComponent {
    #disabled = false;
    #onDisabledFn = null;

    /**
     * @param {JQuery} $originEl 
     * @param {DefaultOptions} options 
     */
    constructor($originEl, options) {
        super();

        const value = options.value ?? DEFAULT_OPTIONS.value;
        options = {
            ...DEFAULT_OPTIONS,
            ...options,
            value: `${value}`
        };

        const parsedTemplate = $.parseHTML(template(options));

        this.$el = $(parsedTemplate);
        this.$originEl = $originEl;

        this.$select = this.$el.find('[data-control="select"]')

        this.replaceOriginEl = options.replaceOriginEl;

        this.reactive({
            value: options.value
        });

        if(options.options && options.options.length === 0) {
            this.disableElement();
        }

        this.init();
    }

    [watch]() {
        return { value: this.updateState };
    }

    init() {
        this.setupListeners();
        this.updateState();
    }

    focus() {
        this.$select.get(0).focus();
    }

    enable() {
        this.#disabled = false;
        this.#onDisabledFn = null;
        this.$el.removeClass(CLASS_NAME_DISABLED);
    }

    enableElement() {
        this.$select.attr('disabled', null);
    }

    disable(onDisabledFn) {
        this.#disabled = true;
        this.$el.addClass(CLASS_NAME_DISABLED);

        if(isFn(onDisabledFn)) {
            this.#onDisabledFn = onDisabledFn;
        }
    }

    disableElement() {
        this.$select.attr('disabled', true);
    }

    highlight(isHighlighted = true) {
        if(isHighlighted) {
            this.$el.addClass(CLASS_NAME_HIGHLIGHT);
        } else {
            this.$el.removeClass(CLASS_NAME_HIGHLIGHT);
        }
    }

    onDisabledUpdate(deferredValue) {
        if(this.#onDisabledFn) {
            const deferredUpdateFn = () => {
                this.$select.val(deferredValue);
                this.value = deferredValue;
            };

            this.#onDisabledFn(deferredUpdateFn);
        }

        this.$select.val(this.value);
    }

    onSelectChange(evt) {
        const value = evt.target.value;

        if(!this.#disabled) {
            this.value = value;
        } else {
            this.onDisabledUpdate(value);
        }
    }

    onValidationChange(evt) {
        const $el = $(evt.target);
        if($el.val()) {
            this.$select.addClass(CLASS_NAME_VALID);
            this.$select.removeClass(CLASS_NAME_INVALID);
        } else {
            this.$select.removeClass(CLASS_NAME_VALID);
            this.$select.addClass(CLASS_NAME_INVALID);
        }
    }

    setOptions({ options, unselectedText }) {
        if(!options || !Array.isArray(options) || options.length === 0) {
            return;
        }

        const optionsHtmlStr = selectOptions({ options, unselectedText });

        this.$select.empty();
        this.$select.html(optionsHtmlStr);
        this.$select.val(this.value);

        this.enableElement();
    }

    setValue(value) {
        const newVal = value ?? `${value}`;
        if(this.value !== newVal) {
            this.value = newVal;
        }
    }

    setInvalid() {
        this.$select.addClass(CLASS_NAME_INVALID);
        this.$select.on(EVENT_VALIDATION, (evt) => this.onValidationChange(evt));
    }

    setValid() {
        this.$select.addClass(CLASS_NAME_VALID);
        this.$select.on(EVENT_VALIDATION, (evt) => this.onValidationChange(evt));
    }

    removeValidation() {
        this.$select.removeClass(CLASS_NAME_INVALID);
        this.$select.removeClass(CLASS_NAME_VALID);
        this.$select.off(EVENT_VALIDATION);
    }

    setupListeners() {
        this.$select.on('change', evt => this.onSelectChange(evt));
    }

    render() {
        if(this.$originEl) {
            if(this.replaceOriginEl) {
                this.$originEl.replaceWith(this.$el);
            } else {
                this.$originEl.html(this.$el);
            }
        }

        return this.$el;
    }

    watchValue(fn) {
        if(isFn(fn)) {
            return this.watch('value', fn);
        }
    }

    updateState() {
        this.$select.val(this.value).attr('data-value', this.value);
    }
}

$(() => {
    $('.js-select-box').each(function() {
        const $el = $(this);

        /** @type {DefaultOptions} */
        const ariaLabel = $el.data('aria-label') || DEFAULT_OPTIONS.ariaLabel;
        const className = $el.data('class-name') || DEFAULT_OPTIONS.className;
        const id = $el.data('id') || DEFAULT_OPTIONS.id;
        const mods = $el.data('mods') || DEFAULT_OPTIONS.mods;
        const replaceOriginEl = typeof $el.data('replace-origin-element') === 'boolean' ? $el.data('replace-origin-element') : DEFAULT_OPTIONS.replaceOriginEl;
        const unselectedText = $el.data('unselected-text') || DEFAULT_OPTIONS.unselectedText;
        const value = $el.data('value') || DEFAULT_OPTIONS.value;

        const $options = $el.find(`[${OPTION_DATA_SELECTOR}],option`);
        const options = $options.map((index) => ({
            text: $options.eq(index).text(),
            value: $options.eq(index).attr('value')
        })).toArray();

        const select = new SelectBox($el, {
            ariaLabel,
            className,
            id,
            mods,
            options,
            replaceOriginEl,
            unselectedText,
            value
        });

        select.render();
    });
});
